// Debounced forecast-persist subscription (EPIC #157, phase P6 — design §4).
//
// Replaces `useForecastPersistence`'s render save-effect (P5.5) with a debounced
// `runStore.subscribe` — the design's "persistence-on-change becomes a store subscription,
// not a render effect." The diff-against-`savedRef` / `_pending` / `detailPending` logic below
// is moved VERBATIM from that effect; the only behavioral changes are the two migration seams:
//   • the trigger — a debounced store subscription instead of a React effect keyed on
//     `[forecasts, authEnabled, forecastsLoaded]`. A store dispatch changes `forecasts` (it's
//     a `useMemo` over `runState`) on the same cadence the old effect's deps fired on, so this
//     preserves the trigger surface; the debounce collapses a dispatch burst (e.g. SSE
//     progress ticks) into one persist pass instead of one per render.
//   • `deletedIdsRef` (a mutable ref queue) → `pendingDeleteIds` in the run-store
//     (`deleteRequested`/`deleteAcked`, dispatched by `commands/deleteForecast.ts`) — P6 moved
//     the delete queue into the store now that the persister is being rewritten anyway.
// `pinned`/the merged watchlist-aware `forecasts` array are NOT store state (pinned lives in
// React state) — `getInputs()` is a live read of both, supplied by the caller (mirrors the
// `runStateRef.current`-style render-time mirror already used throughout this codebase for
// exactly this "React state a command needs to read live" shape).
import { stripDebugForWire } from "../lib/forecast/forecastClient.js";
import { deleteAcked } from "../lib/forecast/reducer.js";
import type { CommandDeps } from "./deps.js";
import { persistForecast } from "./persistForecast.js";

export const DEBOUNCE_MS = 500;

/** Live inputs the persister reads each pass — not store state (see module doc). */
export type PersisterInputs = () => { forecasts: any[]; pinned: Record<string, unknown> };

/** Trailing debounce: collapse a burst of calls into one, `ms` after the last. */
function debounce(fn: () => void, ms: number): { call: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    call: () => {
      if (timer != null) clearTimeout(timer);
      timer = setTimeout(fn, ms);
    },
    cancel: () => {
      if (timer != null) clearTimeout(timer);
      timer = null;
    },
  };
}

/** Baseline-prime one row (the effect's first-pass branch) into `savedRef`/`awaitingDetailRef`. */
function primeRow(f: any, savedRef: Map<string, string>, awaitingDetailRef: Set<string>): void {
  // A lean row gets NO baseline yet: its snapshot lacks the detail blobs, so baselining it now
  // would make the backfill (which fills them in) look like a user edit and re-POST every row
  // on the list. It is parked here instead and baselined the moment its detail lands.
  if (f.detailPending) {
    awaitingDetailRef.add(f.id);
    return;
  }
  if (!f._pending) savedRef.set(f.id, JSON.stringify(stripDebugForWire(f)));
}

/**
 * Install the debounced persister. Primes the baseline immediately from the current
 * `forecasts` (equivalent to the old effect's one-time `baselinePrimedRef` branch — there's no
 * "first pass" ambiguity here since this runs once, at install), then subscribes to the store.
 * Returns an unsubscribe that also cancels any pending debounced pass.
 */
export function installForecastPersister(
  deps: CommandDeps,
  getInputs: PersisterInputs,
): () => void {
  const savedRef = new Map<string, string>();
  const awaitingDetailRef = new Set<string>();

  const { forecasts: initialForecasts } = getInputs();
  initialForecasts.forEach((f) => primeRow(f, savedRef, awaitingDetailRef));

  const persistPass = () => {
    const { forecasts } = getInputs();
    // Drain the pending-delete queue: fire the DELETE cascade for each id the delete
    // commands registered, then ack it out of the store. Unconditional on whether
    // `forecasts` changed this pass — a delete already removed the row from `runsById`.
    const pendingDeleteIds = deps.store.getState().pendingDeleteIds;
    for (const id of pendingDeleteIds) {
      savedRef.delete(id);
      void deps.services.api.deleteForecast(id);
      deps.store.dispatch(deleteAcked(id));
    }
    const live = new Set(forecasts.map((f) => f.id));
    // Quiet sweep: ids that vanished without an explicit delete (e.g. placeholder cleanup on
    // cancel) are dropped from savedRef so we don't re-POST them, but the server row stays —
    // the reaper handles stale running rows.
    for (const k of savedRef.keys()) {
      if (!live.has(k)) savedRef.delete(k);
    }
    forecasts.forEach((f) => {
      // Derived branches are inserted authoritatively by /derived-branch. Sending
      // them through the generic content upsert would call allocateAndSync and
      // create the data-plane contract this row intentionally never has.
      if (f.forkDerived) return;
      // Never persist an in-flight (_pending) forecast as content — see persistForecast.ts's
      // sibling save path for the full rationale (bug 5d54d995).
      if (f._pending) return;
      // Never persist a run whose detail blobs haven't arrived yet — boot hydration takes the
      // LEAN list and backfills detail a moment later; a save landing inside that window would
      // wipe those columns on the server.
      if (f.detailPending) {
        awaitingDetailRef.add(f.id);
        return;
      }
      const snap = JSON.stringify(stripDebugForWire(f));
      // Detail just landed for a boot-hydrated row: adopt the now-complete snapshot as the
      // baseline and DON'T POST — the row's content came from the server this instant.
      // …but only when the detail really did come from the BACKFILL. A row whose run
      // completed locally inside the boot window carries `detailLocal`, and adopting it as a
      // baseline here would drop the user's fresh result on the floor unsaved. Unpark it and
      // let it take the ordinary diff-and-POST path.
      if (awaitingDetailRef.has(f.id)) {
        awaitingDetailRef.delete(f.id);
        if (!f.detailLocal) {
          savedRef.set(f.id, snap);
          return;
        }
      }
      if (savedRef.get(f.id) === snap) return;
      savedRef.set(f.id, snap);
      persistForecast(deps, f, snap);
    });
  };

  const debounced = debounce(persistPass, DEBOUNCE_MS);
  const unsubscribeStore = deps.store.subscribe(debounced.call);
  return () => {
    debounced.cancel();
    unsubscribeStore();
  };
}
